import React, { useEffect } from 'react';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';

export interface SubTabsTabDefinition {
  /**
   * This should use <SubTabsHeader />
   * @see SubTabsHeader
   */
  header: ({ isSelected }: { isSelected: boolean }) => React.ReactNode;
  body: () => React.ReactNode;
}

export interface UseSubTabsParams {
  /**
   * @default 0
   */
  initialSelectedTabIndex?: number;
  tabDefinitions: SubTabsTabDefinition[];
}

export interface SubTabsProps {
  tabDefinitions: SubTabsTabDefinition[];
  selectedTabIndex: number;
  onTabChange: (tabIndex: number) => void;
}

export const useSubTabs = ({
  initialSelectedTabIndex = 0,
  tabDefinitions,
}: UseSubTabsParams): SubTabsProps => {
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(
    initialSelectedTabIndex,
  );

  const onTabChange = useCallback(
    (index: number) => {
      if (tabDefinitions[index]) {
        setSelectedTabIndex(index);
      } else {
        // This prevents if you try to go an index that doesn't exist. In that case fallback to initial index
        setSelectedTabIndex(initialSelectedTabIndex);
      }
    },
    [tabDefinitions, initialSelectedTabIndex],
  );

  // This prevents if you're on a tab index, and it dynamically goes away. In that case fallback to initial index
  useEffect(() => {
    if (!tabDefinitions[selectedTabIndex]) {
      setSelectedTabIndex(initialSelectedTabIndex);
    }
  }, [initialSelectedTabIndex, selectedTabIndex, tabDefinitions]);

  return useMemo(() => {
    return {
      tabDefinitions,
      selectedTabIndex,
      onTabChange,
    };
  }, [onTabChange, selectedTabIndex, tabDefinitions]);
};
